import React, { useEffect, useState, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner,
  faCheck,
  faTimes,
  faUndo,
  faReceipt,
} from "@fortawesome/free-solid-svg-icons";
import {
  listBusinessBookings,
  confirmBooking,
  cancelBooking,
  refundBooking,
} from "../../utils/bookings";
import { useToast } from "../ui/Toast";
import { ErrorMessage } from "../ui/ErrorMessage";
import "../../styles/BookingsManager.css";

const STATUS_FILTERS = [
  { value: "", label: "All bookings" },
  { value: "paid_pending_confirmation", label: "Awaiting confirmation" },
  { value: "confirmed", label: "Confirmed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "refunded", label: "Refunded" },
];

function formatMoney(cents, currency = "usd") {
  if (!Number.isFinite(cents)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: (currency || "usd").toUpperCase(),
  }).format(cents / 100);
}

function formatDate(ts) {
  if (!ts) return "—";
  let d;
  if (ts.toDate) d = ts.toDate();
  else if (ts._seconds) d = new Date(ts._seconds * 1000);
  else d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

/**
 * BookingsManager — business-owner view of bookings on one listing.
 */
export default function BookingsManager({ businessId }) {
  const { showToast } = useToast();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [busyId, setBusyId] = useState(null);

  const refresh = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    setError(null);
    try {
      const items = await listBusinessBookings({
        businessId,
        statusFilter: statusFilter || null,
        limit: 100,
      });
      setBookings(items);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [businessId, statusFilter]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleConfirm = async (b) => {
    setBusyId(b.id);
    try {
      await confirmBooking(b.id);
      showToast("Booking confirmed and payout transferred.", "success");
      await refresh();
    } catch (err) {
      showToast(err?.message || "Couldn't confirm booking.", "error");
    } finally {
      setBusyId(null);
    }
  };

  const handleCancel = async (b) => {
    const reason = window.prompt("Reason for cancelling (visible to traveler):", "");
    if (reason === null) return;
    setBusyId(b.id);
    try {
      await cancelBooking(b.id, reason);
      showToast("Booking cancelled and refunded.", "success");
      await refresh();
    } catch (err) {
      showToast(err?.message || "Couldn't cancel booking.", "error");
    } finally {
      setBusyId(null);
    }
  };

  const handleRefund = async (b) => {
    if (!window.confirm(`Refund ${formatMoney(b.amountCents, b.currency)} to traveler? This reverses the Connect payout too.`)) return;
    const reason = window.prompt("Reason for refund (internal):", "") || "";
    setBusyId(b.id);
    try {
      await refundBooking(b.id, reason);
      showToast("Booking refunded.", "success");
      await refresh();
    } catch (err) {
      showToast(err?.message || "Couldn't refund booking.", "error");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="bookings-manager">
      <div className="bookings-manager__header">
        <h2>
          <FontAwesomeIcon icon={faReceipt} /> Bookings
        </h2>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bookings-manager__filter"
        >
          {STATUS_FILTERS.map((f) => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </select>
      </div>

      {error && <ErrorMessage error={error} title="Couldn't load bookings" />}

      {loading ? (
        <div className="bookings-manager__loading">
          <FontAwesomeIcon icon={faSpinner} spin /> Loading…
        </div>
      ) : bookings.length === 0 ? (
        <p className="bookings-manager__empty">No bookings match this filter yet.</p>
      ) : (
        <ul className="bookings-manager__list">
          {bookings.map((b) => {
            const isPending = b.status === "paid_pending_confirmation";
            const isConfirmed = b.status === "confirmed";
            const ownerNet = (b.amountCents || 0) - (b.commissionCents || 0);
            return (
              <li key={b.id} className={`bookings-manager__item bookings-manager__item--${b.status}`}>
                <div className="bookings-manager__main">
                  <div className="bookings-manager__title">{b.title || "Guild booking"}</div>
                  <div className="bookings-manager__meta">
                    <span className={`bookings-manager__status bookings-manager__status--${b.status}`}>
                      {b.status.replace(/_/g, " ")}
                    </span>
                    <span>{formatMoney(b.amountCents, b.currency)} total</span>
                    <span>{formatMoney(b.commissionCents, b.currency)} commission ({b.commissionPctSnapshot}%)</span>
                    <span>{formatMoney(ownerNet, b.currency)} your payout</span>
                    <span>{formatDate(b.createdAt)}</span>
                  </div>
                  {b.note && (
                    <div className="bookings-manager__note">Note: {b.note}</div>
                  )}
                </div>
                <div className="bookings-manager__actions">
                  {isPending && (
                    <>
                      <button
                        type="button"
                        className="btn-primary"
                        disabled={busyId === b.id}
                        onClick={() => handleConfirm(b)}
                      >
                        <FontAwesomeIcon icon={faCheck} /> Confirm
                      </button>
                      <button
                        type="button"
                        className="btn-danger"
                        disabled={busyId === b.id}
                        onClick={() => handleCancel(b)}
                      >
                        <FontAwesomeIcon icon={faTimes} /> Decline + Refund
                      </button>
                    </>
                  )}
                  {isConfirmed && (
                    <button
                      type="button"
                      className="btn-secondary"
                      disabled={busyId === b.id}
                      onClick={() => handleRefund(b)}
                    >
                      <FontAwesomeIcon icon={faUndo} /> Refund
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
